import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { Button, useDisclosure } from '@heroui/react';
import {
  useGetCourseByIdQuery,
  useGetIntakesQuery,
  useDeleteIntakeMutation,
  useDeleteCourseMutation,
  useCourseDuplicationMutation,
  useHideCourseMutation,
  Intake,
} from '@/entities/Courses';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useToast } from '@/shared/lib/hooks/useToast';
import { MessageModal } from '@/shared/ui/Modal';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import SearchIcon from '@/shared/assets/icons/search.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import EditIcon from '@/shared/assets/icons/edit.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import ClosedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import CopyIcon from '@/shared/assets/icons/copy.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { routerPath } from '@/shared/config/router';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { intakesColumns } from '../../config/intakesColumns';
import { intakesSortOptions } from '../../config/intakesSortOptions';
import { IntakesTableBody } from '../components/IntakesTableBody';
import { AddIntakeModal } from '../components/AddIntakeModal';
import { EditIntakeModal } from '../components/EditIntakeModal';
import { AddGroupModal } from '../components/AddGroupModal';
import { EditCourseModal } from '../components/EditCourseModal';
import styles from './CourseDetailView.module.scss';

interface CourseDetailViewProps {
  courseId: number;
}

export const CourseDetailView = ({ courseId }: CourseDetailViewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canEntity } = useAdminAccess();
  const canCourseCreate = canEntity('course', 'create');
  const canCourseUpdate = canEntity('course', 'update');
  const canCourseDelete = canEntity('course', 'delete');
  const canIntakeCreate = canEntity('intake', 'create');
  const canIntakeUpdate = canEntity('intake', 'update');
  const canIntakeDelete = canEntity('intake', 'delete');
  const canGroupCreate = canEntity('group', 'create');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 8,
  });
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 500);
  const [sort, setSort] = useState<string>('createdAt:desc');
  const [selectedRows, setSelectedRows] = useState<Intake[]>([]);
  const [expandedIntakes, setExpandedIntakes] = useState<Set<number>>(new Set());
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false);
  const [isEditIntakeModalOpen, setIsEditIntakeModalOpen] = useState(false);
  const [selectedIntakeId, setSelectedIntakeId] = useState<number | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isEditCourseModalOpen, setIsEditCourseModalOpen] = useState(false);
  const prevSearchRef = useRef(debouncedSearch);

  const {
    isOpen: isDeleteSelectedModalOpen,
    onOpen: onDeleteSelectedModalOpen,
    onOpenChange: onDeleteSelectedModalOpenChange,
  } = useDisclosure();

  const {
    isOpen: isHideCourseModalOpen,
    onOpen: onHideCourseModalOpen,
    onOpenChange: onHideCourseModalOpenChange,
  } = useDisclosure();

  const {
    isOpen: isDeleteCourseModalOpen,
    onOpen: onDeleteCourseModalOpen,
    onOpenChange: onDeleteCourseModalOpenChange,
  } = useDisclosure();

  const [deleteIntake] = useDeleteIntakeMutation();
  const [deleteCourse, { isLoading: isDeletingCourse }] = useDeleteCourseMutation();
  const [duplicateCourse, { isLoading: isDuplicatingCourse }] = useCourseDuplicationMutation();
  const [hideCourse, { isLoading: isHidingCourse }] = useHideCourseMutation();

  const { data: courseData, isLoading: isCourseLoading } = useGetCourseByIdQuery(courseId);
  const { data: intakesData, isLoading: isIntakesLoading } = useGetIntakesQuery(
    {
      courseId,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      search: debouncedSearch || undefined,
      sort: sort !== 'default' ? sort : undefined,
    },
    {
      skip: false,
    },
  );

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const intakes = useMemo(() => intakesData?.data?.intakes || [], [intakesData?.data?.intakes]);
  const paginationMeta = intakesData?.data?.pagination;
  const total = intakesData?.data?.intakesCount || paginationMeta?.total || 0;
  const course = courseData?.data?.course;

  const handleSortChange = (keys: any) => {
    const newValue = new Set(keys).values().next().value || 'createdAt:desc';
    setSort(newValue as string);
  };

  const handleExport = () => {
    // TODO: Implement export intakes
  };

  const handleAddNewIntake = () => {
    setIsIntakeModalOpen(true);
  };

  const handleIntakeModalSuccess = () => {
    setIsIntakeModalOpen(false);
    // Refetch intakes will happen automatically due to invalidatesTags
  };

  const handleAddNewGroup = () => {
    setIsGroupModalOpen(true);
  };

  const handleGroupModalSuccess = () => {
    setIsGroupModalOpen(false);
    // Refetch intakes will happen automatically due to invalidatesTags
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    onDeleteSelectedModalOpen();
  };

  const handleConfirmDeleteSelected = async () => {
    try {
      await Promise.all(selectedRows.map((intake) => deleteIntake(intake.id).unwrap()));
      toast({
        title: 'Success',
        description: `${selectedRows.length} intake(s) deleted successfully`,
        color: 'success',
      });
      setSelectedRows([]);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete selected intakes',
        color: 'danger',
      });
    }
    onDeleteSelectedModalOpenChange();
  };

  const handleSelectedItems = (rows: Intake[]) => {
    setSelectedRows(rows);
  };

  const handleEditCourseSuccess = () => {
    setIsEditCourseModalOpen(false);
  };

  const handleHideCourse = async () => {
    try {
      const isVisible = !course?.isVisible;
      await hideCourse({ id: courseId, body: { isVisible } }).unwrap();
      toast({
        title: 'Success',
        description: isVisible ? 'Course shown successfully' : 'Course hidden successfully',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to update course visibility',
        color: 'danger',
      });
    }
    onHideCourseModalOpenChange();
  };

  const handleDeleteCourse = async () => {
    try {
      await deleteCourse(courseId).unwrap();
      toast({
        title: 'Success',
        description: 'Course deleted successfully',
        color: 'success',
      });
      onDeleteCourseModalOpenChange();
      navigate(routerPath.courses);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete course',
        color: 'danger',
      });
      onDeleteCourseModalOpenChange();
    }
  };

  const handleDuplicateCourse = async () => {
    try {
      const result = await duplicateCourse({ courseId }).unwrap();
      toast({
        title: 'Success',
        description: result.message || 'Course duplicated successfully',
        color: 'success',
      });
      const newCourseId = result.data?.courseId;
      if (typeof newCourseId === 'number') {
        navigate(routerPath.courseDetail.replace(':id', String(newCourseId)));
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to duplicate course',
        color: 'danger',
      });
    }
  };

  const handleToggleIntake = useCallback((intakeId: number) => {
    setExpandedIntakes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(intakeId)) {
        newSet.delete(intakeId);
      } else {
        newSet.add(intakeId);
      }
      return newSet;
    });
  }, []);

  const tableMeta = paginationMeta
    ? {
      page: paginationMeta.page,
      pageSize: paginationMeta.pageSize,
      total: paginationMeta.total,
      pageCount: paginationMeta.pageCount,
    }
    : undefined;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEditIntake = (intakeId: number) => {
    setSelectedIntakeId(intakeId);
    setIsEditIntakeModalOpen(true);
  };

  const handleEditIntakeSuccess = () => {
    setIsEditIntakeModalOpen(false);
    setSelectedIntakeId(null);
  };

  const columns = useMemo(
    () =>
      intakesColumns({
        onToggleIntake: handleToggleIntake,
        expandedIntakes,
        onEditIntake: handleEditIntake,
        allowEditIntake: canIntakeUpdate,
        allowHideIntake: canIntakeUpdate,
        allowDeleteIntake: canIntakeDelete,
      }),
    [expandedIntakes, handleToggleIntake, handleEditIntake, canIntakeUpdate, canIntakeDelete],
  );

  if (isCourseLoading || isIntakesLoading) {
    return <SectionLoader />;
  }

  if (!course) {
    return null;
  }

  const courseTitle = intakesData?.data?.course?.title || course?.title || '';

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-4">
        <CourseBreadcrumbs
          items={[
            { label: 'Courses', href: routerPath.courses },
            { label: courseTitle || 'Course' },
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="inline-flex gap-x-2 items-end">
            <h2 className="text-[32px] font-medium max-w-[622px]">
              {intakesData?.data?.course?.title || course.title}
            </h2>
            <p className="text-grey-secondary leading-[220%]">
              (found {intakesData?.data?.intakesCount || total} intakes)
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCourseUpdate && (
              <Button
                isIconOnly
                variant="light"
                onPress={() => setIsEditCourseModalOpen(true)}
                className="text-grey-secondary hover:text-black"
              >
                <Svg Icon={EditIcon} width={20} height={20} />
              </Button>
            )}
            {/* Дубликат — новый курс: бэкенд проверяет course.create */}
            {canCourseCreate && (
              <Button
                isIconOnly
                variant="light"
                isLoading={isDuplicatingCourse}
                isDisabled={isDuplicatingCourse}
                onPress={handleDuplicateCourse}
                className="text-grey-secondary hover:text-black"
              >
                <Svg Icon={CopyIcon} width={20} height={20} />
              </Button>
            )}
            {canCourseUpdate && (
              <Button
                isIconOnly
                variant="light"
                onPress={onHideCourseModalOpen}
                className="text-grey-secondary hover:text-black"
              >
                <Svg
                  Icon={course.isVisible ? OpenedEyeIcon : ClosedEyeIcon}
                  width={20}
                  height={20}
                />
              </Button>
            )}
            {canCourseDelete && (
              <Button
                isIconOnly
                variant="light"
                onPress={onDeleteCourseModalOpen}
                className="text-grey-secondary hover:text-black"
              >
                <Svg Icon={TrashIcon} width={20} height={20} className="text-icon-dizlike" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="w-full max-w-[530px]">
              <CustomInput
                className={searchInputClassName}
                startContent={<Svg Icon={SearchIcon} />}
                placeholder="Search"
                value={search}
                onValueChange={setSearch}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-4">
                {canIntakeCreate && (
                  <Button
                    className="bg-[#211D55] text-white !rounded-[8px]"
                    onPress={handleAddNewIntake}
                  >
                    Add new intake
                  </Button>
                )}
                {canGroupCreate && (
                  <Button
                    variant="bordered"
                    className="border-[#211D55] bg-white text-[#211D55] !rounded-[8px]"
                    onPress={handleAddNewGroup}
                  >
                    Add new group
                  </Button>
                )}
              </div>
              <Button
                variant="bordered"
                className="border-[#211D55] bg-white text-[#211D55] !rounded-[8px] !w-full"
                onPress={handleExport}
              >
                Export
              </Button>
            </div>
          </div>

          <div className="flex flex-col items-end justify-between gap-4">
            <SelectSort
              sections={intakesSortOptions}
              defaultValue={sort}
              onSelectionChange={handleSortChange}
            />
            <div className="w-full flex  items-center gap-4">
              {selectedRows.length > 0 && canIntakeDelete && (
                <div className="w-full flex justify-between items-center gap-4">
                  <span className="text-grey-secondary">Selected: {selectedRows.length}</span>
                  <Button
                    variant="light"
                    className="bg-white text-red-tertiary sizse-[16px]"
                    onPress={handleDeleteSelected}
                    endContent={<Svg Icon={TrashIcon} width={20} height={20} />}
                  >
                    Delete ({selectedRows.length})
                  </Button>
                </div>
              )}
            </div>
          </div>

          <ContainerTable<Intake>
            data={intakes}
            meta={tableMeta}
            columns={columns}
            pagination={pagination}
            setPagination={setPagination as OnChangeFn<PaginationState>}
            hideTopActions={true}
            onSelectedItemsChange={handleSelectedItems}
            customTableBody={(rows) => (
              <IntakesTableBody
                rows={rows}
                expandedIntakes={expandedIntakes}
                onToggleIntake={handleToggleIntake}
                courseId={courseId}
              />
            )}
          />
        </div>
      </Container>
      <AddIntakeModal
        isOpen={isIntakeModalOpen}
        onOpenChange={setIsIntakeModalOpen}
        courseId={courseId}
        onSuccess={handleIntakeModalSuccess}
      />
      {selectedIntakeId && (
        <EditIntakeModal
          isOpen={isEditIntakeModalOpen}
          onOpenChange={setIsEditIntakeModalOpen}
          intakeId={selectedIntakeId}
          courseId={courseId}
          onSuccess={handleEditIntakeSuccess}
        />
      )}
      <AddGroupModal
        isOpen={isGroupModalOpen}
        onOpenChange={setIsGroupModalOpen}
        intakes={intakes}
        onSuccess={handleGroupModalSuccess}
      />
      <MessageModal
        title="Delete Selected Intakes"
        text={`Are you sure you want to delete ${selectedRows.length} selected intake(s)? This action cannot be undone.`}
        confirmText="Delete"
        isOpen={isDeleteSelectedModalOpen}
        onOpenChange={onDeleteSelectedModalOpenChange}
        variant="red"
        toggleVisibility={handleConfirmDeleteSelected}
        isLoading={false}
      />
      <EditCourseModal
        isOpen={isEditCourseModalOpen}
        onOpenChange={setIsEditCourseModalOpen}
        courseId={courseId}
        onSuccess={handleEditCourseSuccess}
      />
      <MessageModal
        title={course.isVisible ? 'Hide Course' : 'Show Course'}
        text={`Are you sure you want to ${course.isVisible ? 'hide' : 'show'} the course "${course.title}"?`}
        confirmText={course.isVisible ? 'Hide' : 'Show'}
        isOpen={isHideCourseModalOpen}
        onOpenChange={onHideCourseModalOpenChange}
        variant="black"
        toggleVisibility={handleHideCourse}
        isLoading={isHidingCourse}
      />
      <MessageModal
        title="Delete Course"
        text={`Are you sure you want to delete the course "${course.title}"? This action cannot be undone. All related data (intakes, terms, modules, lessons, groups, etc.) will be permanently deleted.`}
        confirmText="Delete"
        isOpen={isDeleteCourseModalOpen}
        onOpenChange={onDeleteCourseModalOpenChange}
        variant="red"
        toggleVisibility={handleDeleteCourse}
        isLoading={isDeletingCourse}
      />
    </div>
  );
};
